/* =========================================================================
   monthly.ts — Monthly cashflow expansion (development + Y1 ramp)
   Derives from annual ModelResult; does not replace it.
   ========================================================================= */

import type { Scenario, ModelResult, Month, MonthlyResult } from "./types";

const sum = (arr: number[]): number =>
  arr.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);

export function computeMonthly(
  scenario: Scenario,
  result: ModelResult,
): MonthlyResult {
  const a = scenario.assumptions;
  const buildings = scenario.buildings ?? [];
  const devMonths = Math.max(1, Math.round(a.developmentMonths));
  const year1Months = 12;

  // ---- pre-compute live-building aggregates by year (same as annual model) ----
  const liveCapacityByYear = new Array(a.modelYears + 1).fill(0);
  const liveNetRentByYear = new Array(a.modelYears + 1).fill(0);
  const liveOutgoingsByYear = new Array(a.modelYears + 1).fill(0);
  for (const b of buildings) {
    const startYear = Math.max(1, Math.floor(b.comesOnlineYear));
    const cap = Number.isFinite(b.enrolmentCapacity) ? b.enrolmentCapacity : 0;
    const nr =
      Number.isFinite(b.nlaSqm) && Number.isFinite(b.netRentPerSqm)
        ? b.nlaSqm * b.netRentPerSqm
        : 0;
    const og =
      Number.isFinite(b.nlaSqm) && Number.isFinite(b.outgoingsPerSqm)
        ? b.nlaSqm * b.outgoingsPerSqm
        : 0;
    for (let y = startYear; y <= a.modelYears; y++) {
      liveCapacityByYear[y] += cap;
      liveNetRentByYear[y] += nr;
      liveOutgoingsByYear[y] += og;
    }
  }

  // ---- developer cost components ----
  const hardConstruction = result.developer.hardConstruction;
  const preliminaries = result.developer.preliminaries;
  const builderMargin = result.developer.builderMargin;
  const consultantCosts = result.developer.consultantCosts;
  const statutoryCosts = result.developer.statutoryCosts;
  const devContingency = result.developer.constructionContingency;
  const stampDuty = a.purchasePrice * a.stampDutyRate;
  const acquisitionFee = a.purchasePrice * a.acquisitionFeeRate;
  const landAcq = a.purchasePrice + stampDuty + acquisitionFee;

  // Approval cost split (Phase G)
  const ssdaCost = a.ssdaCost ?? 0;
  const changeOfUseCost = a.changeOfUseCost ?? 0;
  const otherApprovalCosts = a.otherApprovalCosts ?? 0;
  const totalSplitApprovals = ssdaCost + changeOfUseCost + otherApprovalCosts;
  const reddamSharePct = Math.min(
    1,
    Math.max(0, a.approvalCostReddamSharePct ?? 0.5),
  );
  const developerApprovalShare = result.developer.developerApprovalShare;
  const totalGfa = sum(buildings.map((building) => building.gfaSqm));

  // Per-building construction phasing
  const buildingData = buildings.map((b) => {
    const hard =
      result.developer.costBasisMode === "loaded_aggregate"
        ? hardConstruction *
          (totalGfa > 0 ? b.gfaSqm / totalGfa : 1 / buildings.length)
        : b.gfaSqm * b.constructionPerSqmGfa;
    const hardShare = hardConstruction > 0 ? hard / hardConstruction : 0;
    const consult = consultantCosts * hardShare;
    const statutory = statutoryCosts * hardShare;
    const contingency = devContingency * hardShare;
    const prelim = preliminaries * hardShare;
    const margin = builderMargin * hardShare;
    const startMonth = Math.max(
      0,
      Math.round(
        Number.isFinite(b.constructionStartMonth)
          ? b.constructionStartMonth!
          : 0,
      ),
    );
    const durationMonths = Math.max(
      1,
      Math.round(
        Number.isFinite(b.constructionMonths)
          ? b.constructionMonths!
          : a.developmentMonths,
      ),
    );
    const completionMonth = startMonth + durationMonths;
    return {
      hard,
      consult,
      statutory,
      contingency,
      prelim,
      margin,
      startMonth,
      durationMonths,
      completionMonth,
    };
  });
  const lastCompletionMonth =
    buildingData.length > 0
      ? Math.max(...buildingData.map((b) => b.completionMonth))
      : devMonths;
  const totalMonths =
    lastCompletionMonth + year1Months + (a.modelYears - 1) * 12;

  const amenityConstruction = result.developer.amenityConstruction;
  const siteWorksConstruction = result.developer.siteWorksConstruction;
  const holdingInterest = result.developer.holdingInterest;
  // DM fee sourced from the annual model so the configurable fee base (land / finance inclusions)
  // stays in lockstep — do NOT recompute here (was `preMgmt * rate`, which ignored the inclusions).
  const devMgmtFee = result.developer.developmentManagementFee;

  // ---- development drawdown schedule ----
  // Land: month 1
  // Per-building construction: linear over each building's own duration
  // Site-wide soft costs: final month of project
  // Dev mgmt fee: final month of project
  const monthlyLand = landAcq;
  const finalSoft =
    result.developer.planningApprovalCosts +
    developerApprovalShare +
    a.otherDdCosts +
    a.tenantRelocationCosts +
    siteWorksConstruction +
    amenityConstruction;
  const finalMgmt = devMgmtFee;

  const reddamApprovalShare = totalSplitApprovals * reddamSharePct;
  const reddamCapexContribution = Math.max(
    0,
    Number.isFinite(a.reddamCapexContribution) ? a.reddamCapexContribution : 0,
  );
  const totalOpCapex =
    a.reddamFitoutAllowance +
    sum(buildings.map((b) => b.reddamCapex)) +
    reddamApprovalShare +
    reddamCapexContribution;

  // ---- build monthly array ----
  const months: Month[] = [];
  let cumulative = totalOpCapex === 0 ? 0 : -totalOpCapex;

  for (let m = 1; m <= totalMonths; m++) {
    let phase: Month["phase"] = "steady";
    let year = 0;
    let label = "";
    let devOut = 0;
    let devIn = 0;
    let redOut = 0;
    let redRev = 0;
    let redRent = 0;
    let redOpex = 0;
    let enrolment: number | undefined;
    let utilisation: number | undefined;
    let notes = "";
    let netIncome = 0; // accrual net income (display row)
    let operatingCash = 0; // after-tax operating cash (drives cumulative; amort added back) — P4-R1

    if (m <= lastCompletionMonth) {
      // Development phase
      phase = "development";
      label = `Dev M${m}`;
      // Per-building costs: linear over each building's own window
      let monthlyBld = 0;
      for (const b of buildingData) {
        if (m > b.startMonth && m <= b.completionMonth) {
          monthlyBld +=
            (b.hard +
              b.consult +
              b.statutory +
              b.contingency +
              b.prelim +
              b.margin) /
            b.durationMonths;
        }
      }
      // Site-wide holding interest spread over project duration
      const monthlyHolding = holdingInterest / lastCompletionMonth;
      devOut = monthlyBld + monthlyHolding;
      if (m === 1) {
        devOut += monthlyLand;
        notes = "Land acquisition";
      }
      if (m === lastCompletionMonth) {
        devOut += finalSoft + finalMgmt;
        notes = notes ? notes + "; completion" : "Completion";
      }
      // No operating cashflow during dev
    } else if (m <= lastCompletionMonth + year1Months) {
      // Year 1 — flat monthly proration of annual cashflow (schools collect fees in terms)
      phase = "ramp";
      year = 1;
      const ym = m - lastCompletionMonth; // 1..12
      label = `Y1 M${ym}`;
      const yearData = result.years[0];
      if (yearData) {
        enrolment = yearData.enrolment;
        utilisation = yearData.utilisation;
        redRev = yearData.totalRevenue / 12;
        redRent = (yearData.rentGross + yearData.amenityRent) / 12; // building + amenity LL rent (P4-R2)
        redOpex =
          (yearData.variableCosts +
            yearData.fixedOpex +
            yearData.fitoutAmortisation) /
          12;
        netIncome = yearData.reddamNetIncome / 12;
        operatingCash = yearData.reddamOperatingCashflow / 12;
        redOut = redRent + redOpex + yearData.tax / 12;
        notes = "Prorated annual";
      }
    } else {
      // Years 2+ are annual roll-ups; retain the ramp label until the
      // scenario's steady-state year so period frequency and lifecycle stage
      // remain independent.
      const y = Math.floor((m - lastCompletionMonth - 1) / 12) + 1;
      year = y;
      phase = y < a.rampYears ? "ramp" : "steady";
      const monthInYear = ((m - lastCompletionMonth - 1) % 12) + 1;
      if (monthInYear === 1) {
        label = `Y${y}`;
        const yearData = result.years[y - 1];
        if (yearData) {
          enrolment = yearData.enrolment;
          utilisation = yearData.utilisation;
          redRev = yearData.totalRevenue;
          redRent = yearData.rentGross + yearData.amenityRent; // building + amenity LL rent (P4-R2)
          redOpex =
            yearData.variableCosts +
            yearData.fixedOpex +
            yearData.fitoutAmortisation;
          netIncome = yearData.reddamNetIncome;
          operatingCash = yearData.reddamOperatingCashflow;
          redOut = redRent + redOpex + yearData.tax;
          notes = "Annual total";
        }
      } else {
        // Skip non-first months of steady-state years (compact view)
        continue;
      }
    }

    cumulative += operatingCash; // cash trajectory (amort added back) — mirrors annual cumulativeCashflow

    months.push({
      month: m,
      year,
      phase,
      label,
      developerOutflow: devOut,
      developerInflow: devIn,
      reddamOutflow: redOut,
      reddamRevenue: redRev,
      reddamRent: redRent,
      reddamOpex: redOpex,
      reddamNetIncome: netIncome,
      cumulativeCashflow: cumulative,
      enrolment,
      utilisation,
      notes,
    });
  }

  return { months, developmentMonths: lastCompletionMonth, year1Months };
}
